import React, { useState, useEffect, useRef } from "react";
import { auth, db, storage } from "../firebase";
import { doc, getDoc, setDoc, getDocs, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import StudentDashboard from "./StudentDashboard";
import useSettings from "../hooks/useSettings";
import { useToast, ToastContainer } from "../hooks/useToast";
import useSessionTimeout from "../hooks/useSessionTimeout";
import BlackoutPicker from "../components/BlackoutPicker";
import { fireConfetti } from "../utils/celebrate";

const MAROON = "#6f0000";

function fmt12(t) {
  if (!t) return "";
  if (/am|pm/i.test(t)) return t;
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

const ROLE_CATEGORIES = [
  {
    category: "Energy / Commodity Interest",
    roles: [
      ["Natural Gas", "naturalGas"],
      ["Crude Oil", "crudeOil"],
      ["LNG", "lng"],
      ["Refined Products", "refinedProducts"],
      ["Power", "power"],
      ["Renewables", "renewables"],
      ["Petrochemicals", "petrochemicals"],
    ]
  },
  {
    category: "Front Office Roles",
    roles: [
      ["Fundamental Analyst", "fundamentalAnalyst"],
      ["Trade Support", "tradeSupport"],
      ["Scheduler", "scheduler"],
      ["Origination and/or Structuring", "origination"],
    ]
  },
  {
    category: "Middle Office Roles",
    roles: [
      ["Market Risk Analyst", "marketRisk"],
      ["P&L Analyst", "pnlAnalyst"],
    ]
  },
  {
    category: "Back Office Roles",
    roles: [
      ["Settlements Analyst", "settlements"],
      ["Trade Confirmations Analyst", "tradeConfirmations"],
      ["Exchange Clearing Analyst", "exchangeClearing"],
      ["Credit Analyst", "creditAnalyst"],
    ]
  },
];

export const ALL_ROLES = ROLE_CATEGORIES.flatMap(c => c.roles);
const defaultSliders = Object.fromEntries(ALL_ROLES.map(([, key]) => [key, 5]));



const NAV_ITEMS = [
  { id: "home", label: "Dashboard", icon: "⬡" },
  { id: "profile", label: "Profile Info", icon: "◎" },
  { id: "ferpa", label: "FERPA", icon: "◱" },
  { id: "academic", label: "Academic", icon: "◈" },
  { id: "internships", label: "Internships", icon: "◉" },
  { id: "statement", label: "Statement", icon: "▤" },
  { id: "interests", label: "Role Interests", icon: "▦" },
  { id: "documents", label: "Documents", icon: "◆" },
  { id: "blackout", label: "Blackout Times", icon: "⊘", requiresApproval: true },
  { id: "schedule", label: "My Schedule", icon: "◫", scheduleOnly: true },
  { id: "results", label: "Draft Results", icon: "◈", resultsOnly: true },
];

const SECTION_ORDER = ["home","profile","ferpa","academic","internships","statement","interests","documents"];


export default function StudentPortal() {
  const { settings } = useSettings();
  const GROUP_OPTIONS = (settings.groups || []).map(g => ({ value: g.value, label: `${g.label} — ${g.description}` }));
  const [activeSection, setActiveSectionRaw] = useState("home");
  const [internshipType, setInternshipType] = useState("g16_all"); // default form.group is "16", which always completes all three internships
  const [mySchedule, setMySchedule] = useState([]);
  const [schedulePublished, setSchedulePublished] = useState(false);
  const [presentationsPublished, setPresentationsPublished] = useState(false);
  const [myPresentationSlot, setMyPresentationSlot] = useState(null);
  const [draftResultsPublished, setDraftResultsPublished] = useState(false);
  const [myDraftResults, setMyDraftResults] = useState([]);
  const [draftNextRoundDate, setDraftNextRoundDate] = useState("");
  const [confettiFiredCount, setConfettiFiredCount] = useState(0);
  const [form, setForm] = useState({
    firstName:"",lastName:"",undergradDegree:"",minor:"",
    seekingMFM:"MFM",expectedGraduation:"",group:"16",
    springInternship:"",springInternshipCompany:"",
    summerInternship:"",summerInternshipCompany:"",
    investmentDecision:"",personalStatement:"",presentationLink:"",
    ferpaConsent:"",
    resumeURL:"",photoURL:"",
    classYear:"",hometown:"",continueTrip:"",springException:"",
    ...defaultSliders,
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [ferpaUIN, setFerpaUIN] = useState("");
  const [ferpaChecked, setFerpaChecked] = useState(false);
  const [ferpaSubmitting, setFerpaSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { toasts, showToast, dismiss } = useToast();
  const [charCount, setCharCount] = useState(0);
  const [focusedInput, setFocusedInput] = useState(null);
  const navigate = useNavigate();
  const user = auth.currentUser;
  const mainRef = useRef(null);
  const autoSaveRef = useRef(null);
  const { countdown: sessionCountdown } = useSessionTimeout(() => { signOut(auth); navigate("/august"); });

  function setActiveSection(id) {
    setActiveSectionRaw(id);
    if (mainRef.current) mainRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }

const [globalLocked, setGlobalLocked] = useState(false);
  const [studentUnlocked, setStudentUnlocked] = useState(false);
  const [websiteLocked, setWebsiteLocked] = useState(false);
  const isLocked = globalLocked && !studentUnlocked;
  useEffect(() => {
    getDoc(doc(db, "metadata", "publishStatus")).then(snap => {
      if (snap.exists()) {
        const pub = snap.data();
        setGlobalLocked(pub.submissionsLocked || false);
        setWebsiteLocked(pub.websiteLocked || false);
        if (pub.scheduleStudents) setSchedulePublished(true);
        if (pub.presentations) setPresentationsPublished(true);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || !form.firstName || !form.lastName) return;
    getDoc(doc(db, "metadata", "draftResults")).then(snap => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (!data.published) return;
      setDraftResultsPublished(true);
      setDraftNextRoundDate(data.nextRoundDate || "");
      const myName = (form.firstName + " " + form.lastName).trim();
      const picks = (data.picks || []).filter(p => p.studentName.toLowerCase() === myName.toLowerCase());
      setMyDraftResults(picks);
    }).catch(() => {});
  }, [user, form.firstName, form.lastName]); // eslint-disable-line

  const [announcements, setAnnouncements] = useState([]);
  useEffect(() => {
    getDocs(collection(db, "announcements")).then(snap => {
      const now = new Date(); const active = snap.docs.map(d=>({id:d.id,...d.data()})).filter(a=>a.active&&(a.target==="students"||a.target==="all")&&(!a.expiresAt||new Date(a.expiresAt)>now));
      setAnnouncements(active.sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)));
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "students", user.uid)).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setForm(f => ({ ...f, ...data }));
        // Group 16 always completes all three internships now — no picker,
        // so normalize any previously-saved 1 or 2 selection up to all three.
        if (data.group === "16") setInternshipType("g16_all");
        else if (data.internshipType) setInternshipType(data.internshipType);
        if (data.personalStatement) setCharCount(data.personalStatement.length);
        if (data.photoURL) setPhotoPreview(data.photoURL);
        if (data.submissionLocked === false) setStudentUnlocked(true);
        if (data.ferpaUIN) setFerpaUIN(data.ferpaUIN);
      }
    });
  }, [user]);

  useEffect(() => {
    if (!schedulePublished || !form.firstName || !form.lastName) return;
    getDoc(doc(db, "metadata", "scheduleData")).then(snap => {
      if (snap.exists() && snap.data().studentSchedules) {
        const ss = snap.data().studentSchedules;
        const fn = (form.firstName + ' ' + form.lastName).trim();
        const k = Object.keys(ss).find(x => x.toLowerCase() === fn.toLowerCase());
        if (k) setMySchedule(ss[k]);
      }
    }).catch(() => {});
  }, [schedulePublished, user, form.firstName, form.lastName]);

  useEffect(() => {
    if (!presentationsPublished || !user) return;
    getDoc(doc(db, "metadata", "presentationSchedule")).then(snap => {
      if (!snap.exists()) return;
      const entries = snap.data().entries || [];
      const mine = entries.find(e => e.studentId === user.uid);
      if (mine) setMyPresentationSlot(mine);
    }).catch(() => {});
  }, [presentationsPublished, user]);

  // Auto-save: 3 seconds after last form change
  useEffect(() => {
    if (!unsaved || isLocked || activeSection === "home" || saving) return;
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => handleSave(null, null, true), 3000);
    return () => clearTimeout(autoSaveRef.current);
  }, [form, unsaved]); // eslint-disable-line

  // Ctrl+S / Cmd+S shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s" && activeSection !== "home") {
        e.preventDefault();
        handleSave(null, null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeSection]); // eslint-disable-line

  function isSectionDone(id) {
    switch (id) {
      case "profile":
        return !!(form.firstName && form.lastName && form.undergradDegree && form.classYear && form.hometown && form.shirtSize);
      case "ferpa":
        return !!(form.ferpaConsentStatus === "signed");
      case "academic":
        if (form.group === "16") return true;
        return !!(form.continueTrip);
      case "internships": {
        if (form.group === "16") return !!(form.springInternshipCompany);
        if (form.group === "17") {
          const hasLink = !!(form.presentationLink);
          if (internshipType === "summer") return !!(form.summerInternshipCompany) && hasLink;
          if (internshipType === "both") return !!(form.springInternshipCompany && form.summerInternshipCompany) && hasLink;
          return !!(form.springInternshipCompany) && hasLink;
        }
        if (form.group === "18") return !!form.presentationTopic && (internshipType === "investment" ? !!form.investmentDecision : !!form.summerInternshipCompany);
        if (internshipType === "summer") return !!(form.summerInternshipCompany);
        if (internshipType === "both") return !!(form.springInternshipCompany && form.summerInternshipCompany);
        return !!(form.springInternshipCompany);
      }
      case "statement":
        return !!(form.personalStatement && form.personalStatement.length >= 50);
      case "interests":
        return ALL_ROLES.some(([, key]) => form[key] !== 5);
      case "documents":
        return !!(form.resumeURL);
      default:
        return false;
    }
  }
  // Group 16's "academic" section has no fields to fill (auto-complete), so it's
  // excluded here — otherwise a brand-new Group 16 student would show >0% done.
  const coreSections = form.group === "16"
    ? ["profile", "ferpa", "internships", "statement", "interests", "documents"]
    : ["profile", "ferpa", "academic", "internships", "statement", "interests", "documents"];
  const psMaxLen = form.group === "16" ? 400 : form.group === "18" ? 700 : (internshipType === "both" ? 500 : 650);
  const completionPct = Math.round(coreSections.filter(id => isSectionDone(id)).length / coreSections.length * 100);

  async function handleSave(e, nextSection, isAutosave = false) {
    e && e.preventDefault();
    if (isLocked) { showToast("Submission deadline passed — contact admin to reopen", "error"); return; }
    setSaving(true); setUnsaved(false);
    try {
      let resumeURL = form.resumeURL;
      let photoURL = form.photoURL;
      if (resumeFile) {
        const r = ref(storage, "resumes/" + user.uid);
        await uploadBytes(r, resumeFile);
        resumeURL = await getDownloadURL(r);
      }
      if (photoFile) {
        const r = ref(storage, "photos/" + user.uid);
        await uploadBytes(r, photoFile);
        photoURL = await getDownloadURL(r);
      }
      await setDoc(doc(db, "students", user.uid), {
        ...form, resumeURL, photoURL, internshipType,
        email: user.email, updatedAt: new Date().toISOString()
      });
      setForm(f => ({ ...f, resumeURL, photoURL }));
      if (!isAutosave) showToast("Saved successfully", "success");
      if (nextSection) setActiveSection(nextSection);
    } catch (err) { showToast("Error: " + err.message, "error"); }
    setSaving(false);
  }

  async function handleSubmitAll() {
    if (isLocked) { showToast("Submission deadline passed — contact admin to reopen", "error"); return; }
    const missing = coreSections.filter(id => !isSectionDone(id));
    if (missing.length > 0) {
      const labels = missing.map(id => NAV_ITEMS.find(n => n.id === id)?.label || id);
      showToast(`Please complete: ${labels.join(", ")}`, "error");
      return;
    }
    await handleSave(null, null, true);
    try {
      await setDoc(doc(db, "students", user.uid), {
        applicationSubmitted: true, applicationSubmittedAt: new Date().toISOString(),
      }, { merge: true });
      showToast("Application submitted — you're all set!", "success");
      fireConfetti();
    } catch (err) { showToast("Error: " + err.message, "error"); }
  }

  function updateForm(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    setUnsaved(true);
  }

  const input = (id) => ({
    width: "100%", padding: "11px 14px",
    border: `1.5px solid ${focusedInput === id ? MAROON : "#e8e8e8"}`,
    borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box",
    background: "#fafafa", transition: "all 0.2s",
    boxShadow: focusedInput === id ? "0 0 0 3px rgba(111,0,0,0.08)" : "0 1px 2px rgba(0,0,0,0.04)",
  });
  const lbl = { display: "block", fontSize: 11, fontWeight: 700, marginBottom: 8, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.7 };
  const card = { background: "white", borderRadius: 16, padding: "30px 32px", marginBottom: 22, boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.04)" };
  const nextIdx = SECTION_ORDER.indexOf(activeSection) + 1;
  let nextSect = nextIdx < SECTION_ORDER.length ? SECTION_ORDER[nextIdx] : null;
  if (form.group === "16" && nextSect === "academic") {
    const skipIdx = SECTION_ORDER.indexOf("academic") + 1;
    nextSect = skipIdx < SECTION_ORDER.length ? SECTION_ORDER[skipIdx] : null;
  }

  const internshipOptions = form.group === "16"
    ? [
        { value: "spring", label: "1", title: "One Internship" },
        { value: "g16_two", label: "2", title: "Two Internships" },
        { value: "g16_all", label: "3", title: "Three Internships" },
      ]
    : form.group === "18"
    ? [
        { value: "summer", label: "Su", title: "Summer Internship" },
        { value: "investment", label: "CA", title: "Commodities Analysis" },
      ]
    : [
        { value: "spring", label: "Sp", title: "Spring Internship" },
        { value: "summer", label: "Su", title: "Summer Internship" },
        { value: "both", label: "B", title: "Both Spring & Summer" },
      ];
  const g17DescMax = internshipType === "both" ? 400 : 650;

  if (websiteLocked) return (
    <div style={{ minHeight:"100vh",background:"#0a0a0a",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Inter,-apple-system,sans-serif",padding:24 }}>
      <div style={{ textAlign:"center",maxWidth:420 }}>
        <div style={{ width:64,height:64,borderRadius:"50%",background:"linear-gradient(135deg,#6f0000,#a00000)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 24px",boxShadow:"0 8px 32px rgba(111,0,0,0.4)" }}>🔒</div>
        <div style={{ fontSize:28,fontWeight:900,color:"white",letterSpacing:-1,marginBottom:12 }}>Website Offline</div>
        <div style={{ fontSize:15,color:"rgba(255,255,255,0.45)",lineHeight:1.7 }}>The TRIP platform is currently offline. Please check back later or contact your program administrator.</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#F3F4F6", fontFamily: "Inter, -apple-system, sans-serif" }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes shimmer { from{background-position:200% 0} to{background-position:-200% 0} }
        @keyframes draftPopIn { 0%{opacity:0;transform:scale(0.92) translateY(14px)} 60%{opacity:1;transform:scale(1.015) translateY(0)} 100%{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes draftShine { 0%{transform:translateX(-120%) rotate(8deg)} 100%{transform:translateX(220%) rotate(8deg)} }
        @keyframes draftRingPulse { 0%{transform:scale(0.75);opacity:0.8} 100%{transform:scale(2.4);opacity:0} }
        @keyframes draftTitleGlow { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes draftSparkle { 0%,100%{opacity:0;transform:scale(0.4) rotate(0deg)} 50%{opacity:1;transform:scale(1) rotate(20deg)} }
        @keyframes draftFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .draft-hero { animation: draftPopIn 0.7s cubic-bezier(0.16,1,0.3,1) both; }
        .draft-shine { position:absolute; top:-50%; left:0; width:35%; height:200%; background:linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent); animation: draftShine 2.4s ease-in-out 0.4s 1; pointer-events:none; }
        .draft-ring { position:absolute; inset:0; border-radius:50%; border:1.5px solid rgba(255,90,90,0.55); animation: draftRingPulse 2.2s ease-out infinite; pointer-events:none; }
        .draft-title-glow { background-size:200% auto; animation: draftTitleGlow 3.5s ease infinite; }
        .draft-sparkle { position:absolute; animation: draftSparkle 2.6s ease-in-out infinite; pointer-events:none; }
        /* Faint roaming red fluid inside the hero cards — same background language as the landing page, scaled down to card size */
        @keyframes heroFluidA { 0%{ transform: translate(-10%,-10%) scale(1); } 30%{ transform: translate(70%,15%) scale(1.15); } 60%{ transform: translate(35%,80%) scale(0.9); } 100%{ transform: translate(-10%,-10%) scale(1); } }
        @keyframes heroFluidB { 0%{ transform: translate(80%,70%) scale(1); } 40%{ transform: translate(10%,55%) scale(0.85); } 70%{ transform: translate(-15%,-5%) scale(1.2); } 100%{ transform: translate(80%,70%) scale(1); } }
        .hero-fluid { position:absolute; top:0; left:0; pointer-events:none; z-index:0; mix-blend-mode:screen; will-change:transform; }
        @media (prefers-reduced-motion: reduce) {
          .hero-fluid { animation: none !important; }
        }
        input[type=range] { -webkit-appearance:none; appearance:none; height:6px; border-radius:3px; background:#f0f0f0; cursor:pointer; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:18px; height:18px; border-radius:50%; background:${MAROON}; box-shadow:0 2px 6px rgba(111,0,0,0.3); cursor:pointer; }
        .skeleton { background:linear-gradient(90deg,#f0f2f5 25%,#e4e7ed 50%,#f0f2f5 75%); background-size:200% 100%; animation:shimmer 1.5s ease-in-out infinite; border-radius:12px; }
        .lift { transition: transform 0.18s, box-shadow 0.18s; }
        .lift:hover { transform: translateY(-2px) !important; box-shadow: 0 10px 32px rgba(0,0,0,0.1) !important; }
        .btn-press { transition: transform 0.12s, opacity 0.12s; }
        .btn-press:active { transform: scale(0.97); opacity: 0.88; }
        input:focus, textarea:focus, select:focus { outline:none; border-color:#6f0000 !important; box-shadow:0 0 0 3px rgba(111,0,0,0.1) !important; }
        .sp-mobile-toggle { display:none !important; }
        .sp-sidebar-overlay { display:none; }
        @media (max-width:768px) {
          .sp-sidebar { transform:translateX(-220px); transition:transform 0.28s cubic-bezier(0.4,0,0.2,1) !important; }
          .sp-sidebar.open { transform:translateX(0) !important; }
          .sp-main { margin-left:0 !important; padding:20px 18px !important; }
          .sp-mobile-toggle { display:flex !important; }
          .sp-sidebar-overlay.open { display:block !important; }
        }
      `}</style>
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
      <div className={`sp-sidebar-overlay${sidebarOpen?" open":""}`} onClick={() => setSidebarOpen(false)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:99,backdropFilter:"blur(2px)" }} />
      <button className="sp-mobile-toggle" onClick={() => setSidebarOpen(o=>!o)} style={{ position:"fixed",top:14,left:14,zIndex:200,background:MAROON,border:"none",borderRadius:8,width:38,height:38,cursor:"pointer",color:"white",fontSize:18,alignItems:"center",justifyContent:"center" }}>☰</button>

      {/* Sidebar */}
      <div className={`sp-sidebar${sidebarOpen?" open":""}`} style={{ width: 220, background: "linear-gradient(180deg, #1C0A0A 0%, #2A0A0A 100%)", display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100 }}>
        <div style={{ padding: "26px 18px 22px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, background: `linear-gradient(135deg, ${MAROON}, #a00000)`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: "white", fontSize: 15, boxShadow: "0 4px 10px rgba(111,0,0,0.4)" }}>T</div>
            <div>
              <div style={{ color: "white", fontWeight: 800, fontSize: 15 }}>TRIP</div>
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>Student</div>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 12px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: 1.5, textTransform: "uppercase", padding: "0 8px", marginBottom: 8 }}>Sections</div>
          {NAV_ITEMS.filter(item => {
            if (item.id === "academic" && form.group === "16") return false;
            if (item.requiresApproval && form.blackoutRequestStatus !== "approved") return false;
            if (item.scheduleOnly && (!schedulePublished || form.group === "16")) return false;
            if (item.resultsOnly && !draftResultsPublished && myDraftResults.length === 0) return false;
            return true;
          }).map(item => (
            <div key={item.id} onClick={() => setActiveSection(item.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: activeSection === item.id ? "rgba(255,255,255,0.1)" : "transparent", color: activeSection === item.id ? "white" : "rgba(255,255,255,0.45)", borderLeft: activeSection === item.id ? `2px solid ${MAROON}` : "2px solid transparent", transition: "all 0.15s" }}
              onMouseOver={e => { if (activeSection !== item.id) { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}}
              onMouseOut={e => { if (activeSection !== item.id) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.45)"; }}}>
              <span style={{ fontSize: 13 }}>{item.icon}</span>
              <span style={{ fontSize: 13, fontWeight: activeSection === item.id ? 600 : 400 }}>{item.id === "internships" && form.group === "18" ? "Presentation" : item.label}</span>
            </div>
          ))}
        </div>

        <div style={{ padding: "14px 12px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "14px", marginBottom: 10, display:"flex", alignItems:"center", gap:12 }}>
            {(() => {
              const r = 22, stroke = 4, circ = 2 * Math.PI * r;
              const offset = circ - (completionPct / 100) * circ;
              const done = completionPct === 100;
              return (
                <svg width={54} height={54} style={{ flexShrink:0 }}>
                  <circle cx={27} cy={27} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} />
                  <circle cx={27} cy={27} r={r} fill="none" stroke={done?"#22c55e":MAROON} strokeWidth={stroke}
                    strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                    transform="rotate(-90 27 27)" style={{ transition:"stroke-dashoffset 0.6s ease" }} />
                  <text x={27} y={31} textAnchor="middle" fill={done?"#86efac":"white"} fontSize={10} fontWeight={700}>{completionPct}%</text>
                </svg>
              );
            })()}
            <div>
              <div style={{ color:"rgba(255,255,255,0.4)",fontSize:10,textTransform:"uppercase",letterSpacing:0.8,marginBottom:2 }}>Profile</div>
              <div style={{ color:completionPct===100?"#86efac":"white",fontSize:12,fontWeight:700 }}>{completionPct===100?"Complete":"In progress"}</div>
            </div>
          </div>
          <div onClick={() => { signOut(auth); navigate("/august"); }}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, cursor: "pointer", color: "rgba(255,255,255,0.3)", fontSize: 13, transition: "all 0.15s" }}
            onMouseOver={e => { e.currentTarget.style.color = "rgba(255,255,255,0.7)"; e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
            onMouseOut={e => { e.currentTarget.style.color = "rgba(255,255,255,0.3)"; e.currentTarget.style.background = "transparent"; }}>
            → Sign Out
          </div>
        </div>
      </div>

      {/* Main */}
      <div ref={mainRef} className="sp-main" style={{ marginLeft: 220, flex: 1, padding: "32px 44px", overflowY: "auto", position: "relative" }}>
        {isLocked && activeSection !== "schedule" && (
          <div style={{ position: "sticky", top: 0, zIndex: 50, background: "#fff1f0", border: "1px solid #fca5a5", borderRadius: 10, padding: "12px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 600, color: "#991b1b" }}>
            🔒 Submissions are locked. Contact your admin to reopen.
          </div>
        )}
        {isLocked && activeSection !== "schedule" && <div style={{ position: "absolute", inset: 0, zIndex: 40, cursor: "not-allowed" }} onClick={() => showToast("Submissions locked — contact admin to reopen", "error")} />}

        {/* Top progress bar */}
        {activeSection !== "home" && (
          <div style={{ background: "white", borderRadius: 14, padding: "16px 24px", marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", display: "flex", alignItems: "center", gap: 0, overflowX: "auto" }}>
            {(() => {
              const filteredNav = NAV_ITEMS.filter(item => {
                if (item.id === "home") return false;
                if (item.id === "academic" && form.group === "16") return false;
                if (item.requiresApproval && form.blackoutRequestStatus !== "approved") return false;
                if (item.scheduleOnly && (!schedulePublished || form.group === "16")) return false;
                if (item.resultsOnly && !draftResultsPublished && myDraftResults.length === 0) return false;
                return true;
              });
              return filteredNav.map((item, i) => {
                const isActive = activeSection === item.id;
                const isDone = isSectionDone(item.id);
                return (
                  <React.Fragment key={item.id}>
                    <div onClick={() => setActiveSection(item.id)}
                      style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", flexShrink: 0, padding: "4px 8px", borderRadius: 8, transition: "background 0.15s" }}
                      onMouseOver={e => e.currentTarget.style.background = "#f8f8f8"}
                      onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: isDone ? "#22c55e" : isActive ? MAROON : "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: isDone || isActive ? "white" : "#aaa", fontWeight: 800, transition: "all 0.2s", flexShrink: 0 }}>
                        {isDone ? "✓" : i + 1}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: isActive ? 700 : 500, color: isDone ? "#166534" : isActive ? "#1a1a1a" : "#aaa", whiteSpace: "nowrap" }}>{item.id === "internships" && form.group === "18" ? "Presentation" : item.label}</span>
                    </div>
                    {i < filteredNav.length - 1 && <div style={{ flex: 1, height: 2, background: isDone ? "linear-gradient(90deg,#22c55e,#86efac)" : "#f0f0f0", margin: "0 4px", borderRadius: 2, minWidth: 12 }} />}
                  </React.Fragment>
                );
              });
            })()}
          </div>
        )}

        {/* HOME */}
        {activeSection === "home" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            {announcements.length > 0 && (
              <div style={{ marginBottom:20 }}>
                {announcements.map(ann => (
                  <div key={ann.id} style={{ background:"linear-gradient(135deg,#fff8f0,#fff3e0)",border:"1px solid #fbbf24",borderRadius:12,padding:"14px 18px",marginBottom:10 }}>
                    <div style={{ fontWeight:700,fontSize:14,color:"#92400e",marginBottom:4 }}>📢 {ann.title}</div>
                    <div style={{ fontSize:13,color:"#78350f",lineHeight:1.6 }}>{ann.body}</div>
                  </div>
                ))}
              </div>
            )}
            <StudentDashboard form={form} completionPct={completionPct} setActiveSection={setActiveSection} isSectionDone={isSectionDone} />
          </div>
        )}

        {/* PROFILE */}
        {activeSection === "profile" && (
          <form onSubmit={e => handleSave(e, nextSect)} style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Profile Info</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Your personal and academic information</p>
            </div>
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 18 }}>Personal Details</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                {[["First Name","firstName"],["Last Name","lastName"],["Undergraduate Degree","undergradDegree"],["Minor","minor"]].map(([label, key]) => (
                  <div key={key}>
                    <label style={lbl}>{label}</label>
                    {(key === "firstName" || key === "lastName") ? (
                      <div style={{ ...input(key), background: "#f5f5f5", color: "#555", cursor: "not-allowed", display: "flex", alignItems: "center" }}>
                        {form[key] || <span style={{ color: "#aaa" }}>Set by admin</span>}
                      </div>
                    ) : (
                      <input value={form[key]} onChange={e => updateForm(key, e.target.value)} style={input(key)}
                        onFocus={() => setFocusedInput(key)} onBlur={() => setFocusedInput(null)} />
                    )}
                  </div>
                ))}
                <div>
                  <label style={lbl}>Email</label>
                  <input value={user?.email || ""} disabled style={{ ...input("email"), background: "#f0f0f0", color: "#aaa" }} />
                </div>
                <div>
                  <label style={lbl}>Hometown (City, State)</label>
                  <input value={form.hometown} onChange={e => updateForm("hometown", e.target.value)} placeholder="e.g. Houston, TX" style={input("hometown")}
                    onFocus={() => setFocusedInput("hometown")} onBlur={() => setFocusedInput(null)} />
                </div>
              </div>
            </div>
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 18 }}>Program Info</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <div>
                  <label style={lbl}>In Fall {settings.programYear || "2025"}, I will be:</label>
                  <select value={form.classYear} onChange={e => updateForm("classYear", e.target.value)} style={input("classYear")}
                    onFocus={() => setFocusedInput("classYear")} onBlur={() => setFocusedInput(null)}>
                    <option value="">Select...</option>
                    <option>Junior</option><option>Senior</option><option>Masters</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Shirt Size</label>
                  <select value={form.shirtSize||""} onChange={e => updateForm("shirtSize", e.target.value)} style={input("shirtSize")} onFocus={() => setFocusedInput("shirtSize")} onBlur={() => setFocusedInput(null)}>
                    <option value="">Select size...</option>
                    <option>XS</option><option>S</option><option>M</option><option>L</option><option>XL</option><option>XXL</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Expected Graduation</label>
                  <input type="month" value={form.expectedGraduation} onChange={e => updateForm("expectedGraduation", e.target.value)} style={input("graduation")}
                    onFocus={() => setFocusedInput("graduation")} onBlur={() => setFocusedInput(null)} />
                </div>
                <div>
                  <label style={lbl}>Seeking MFM or Certificate</label>
                  <select value={form.seekingMFM} onChange={e => updateForm("seekingMFM", e.target.value)} style={input("mfm")}
                    onFocus={() => setFocusedInput("mfm")} onBlur={() => setFocusedInput(null)}>
                    <option value="MFM">MFM</option>
                    <option value="Certificate">Certificate</option>
                    <option value="Neither">Neither</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Group</label>
                  <select value={form.group} onChange={e => updateForm("group", e.target.value)} style={input("group")}
                    onFocus={() => setFocusedInput("group")} onBlur={() => setFocusedInput(null)}>
                    {GROUP_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
            {form.ferpaConsentStatus !== "signed" && (
              <div style={{ background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 }}>
                <div style={{ fontSize:13,color:"#92400e",fontWeight:600 }}>FERPA consent required — please complete the FERPA section</div>
                <button type="button" onClick={()=>setActiveSection("ferpa")} style={{ background:"#6f0000",color:"white",border:"none",padding:"8px 16px",borderRadius:8,fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0 }}>Go to FERPA →</button>
              </div>
            )}
            {form.ferpaConsentStatus === "signed" && (
              <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:12,padding:"14px 18px",fontSize:13,color:"#166534",fontWeight:600 }}>
                FERPA agreement signed electronically on {form.ferpaSignedAt ? new Date(form.ferpaSignedAt).toLocaleDateString() : "file"}
              </div>
            )}
            {isLocked && <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600 }}>The submission deadline has passed. Your form is locked. Contact an admin to reopen.</div>}
            <SaveBar saving={saving} unsaved={unsaved} disabled={isLocked} nextLabel="Academic →" />
          </form>
        )}

        {/* FERPA */}
        {activeSection === "ferpa" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>FERPA Consent</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Read the full agreement below, then complete the electronic signature at the bottom.</p>
            </div>
            {form.ferpaConsentStatus === "signed" ? (
              <div style={card}>
                <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:10,padding:"16px 20px",marginBottom:16,fontSize:14,color:"#166534",fontWeight:700 }}>
                  You electronically signed this agreement on {form.ferpaSignedAt ? new Date(form.ferpaSignedAt).toLocaleString() : "record"}.
                </div>
                <div style={{ fontSize:13,color:"#555",marginBottom:8 }}>Name on file: <strong>{form.ferpaSignedName}</strong></div>
                <div style={{ fontSize:13,color:"#555",marginBottom:8 }}>UIN: <strong>{form.ferpaUIN}</strong></div>
                <div style={{ fontSize:13,color:"#555" }}>Email: <strong>{user?.email}</strong></div>
                <div style={{ display:"flex", gap:12, marginTop:16 }}>
                  <button type="button" onClick={() => setActiveSection(nextSect || "academic")} style={{ background:`linear-gradient(135deg,${MAROON},#a00000)`, color:"white", border:"none", padding:"13px 28px", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer", boxShadow:"0 4px 14px rgba(111,0,0,0.25)" }}>Save &amp; Continue: Academic →</button>
                  <button type="button" onClick={()=>{ updateForm("ferpaConsentStatus",""); updateForm("ferpaSignedAt",""); setFerpaChecked(false); }} style={{ background:"none",border:"1px solid #e8e8e8",color:"#888",padding:"8px 16px",borderRadius:8,fontSize:12,cursor:"pointer" }}>Revoke &amp; Re-sign</button>
                </div>
              </div>
            ) : (<>
              {/* Agreement text */}
              <div style={{ ...card, padding:0, overflow:"hidden" }}>
                <div style={{ background:"linear-gradient(135deg,#6f0000,#a00000)",padding:"16px 24px" }}>
                  <div style={{ color:"white",fontWeight:800,fontSize:14,letterSpacing:0.2 }}>TRIP STUDENT CONSENT TO RELEASE AND STORE EDUCATIONAL, ACADEMIC, AND PROFESSIONAL MATERIALS</div>
                </div>
                <div style={{ padding:"22px 24px",maxHeight:420,overflowY:"auto",fontSize:13,color:"#374151",lineHeight:1.8 }}>
                  <p style={{ marginBottom:16 }}>I am a student at Texas A&amp;M University and a participant Trading, Risk and Investments Program ("TRIP").</p>
                  <p style={{ marginBottom:16 }}>I understand that certain materials I may choose to provide for TRIP-related professional development, internship placement, recruiting, mentoring, and advisory-board review may include information from my educational records or other personally identifiable information. These materials may include, but are not limited to, my resume, curriculum vitae, unofficial or official transcript, grade information, GPA, academic history, degree plan, enrollment status, TRIP application materials, professional profile, contact information, photograph, work samples, and other documents or information that I choose to upload, submit, or authorize TRIP to share.</p>
                  <p style={{ marginBottom:16 }}>By signing this form, I voluntarily authorize Texas A&amp;M University, Mays Business School, the Adam C. Sinn '00 Department of Finance, the Adam C. Sinn '00 Center for Investment Management, to disclose, make available, store, transmit, and maintain the materials and information described above for the purposes stated below.</p>

                  <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>A. Records and information covered by this consent</div>
                  <p style={{ marginBottom:16 }}>This consent applies to all records, documents, and information that I upload, submit, provide, or authorize TRIP to use. Such records include, without limitation, resumes, transcripts, GPA or grade information, academic standing, enrollment information, degree information, internship materials, professional profile information, and other materials I choose to provide for TRIP-related recruiting, internship, mentoring, professional-development, or advisory-board purposes.</p>

                  <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>B. Persons or entities authorized to receive or access the information</div>
                  <p style={{ marginBottom:8 }}>I authorize disclosure of and access to the above materials and information by the following persons or classes of persons:</p>
                  <ol style={{ paddingLeft:20,marginBottom:16 }}>
                    <li style={{ marginBottom:4 }}>Members of the TRIP Industry Advisory Board;</li>
                    <li style={{ marginBottom:4 }}>Employees, representatives, and designees of companies represented on the TRIP Industry Advisory Board;</li>
                    <li style={{ marginBottom:4 }}>Faculty, staff, and administrators, affiliated with the Adam C. Sinn '00 Center for Investment Management;</li>
                    <li style={{ marginBottom:4 }}>Other Texas A&amp;M University or Mays Business School employees, including student employees, with a legitimate educational need to assist with program administration, recruiting, internship placement, mentoring, professional development, or advisory-board activities.</li>
                  </ol>
                  <p style={{ marginBottom:16 }}>I understand that TRIP Industry Advisory Board members and their company representatives are not necessarily Texas A&amp;M University employees.</p>

                  <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>C. Purpose of the disclosure, access, and storage</div>
                  <p style={{ marginBottom:8 }}>These records and materials are being disclosed, accessed, stored, and used for TRIP-related purposes, including but not limited to:</p>
                  <ol style={{ paddingLeft:20,marginBottom:16 }}>
                    <li style={{ marginBottom:4 }}>Internship, employment, recruiting, and placement consideration;</li>
                    <li style={{ marginBottom:4 }}>Review by TRIP Industry Advisory Board members and affiliated company representatives;</li>
                    <li style={{ marginBottom:4 }}>Mentoring, advising, and professional-development opportunities;</li>
                    <li style={{ marginBottom:4 }}>Program administration and communication;</li>
                    <li style={{ marginBottom:4 }}>Evaluation of student qualifications for TRIP-related opportunities.</li>
                  </ol>

                  <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>D. Consent to website storage and platform use</div>
                  <p style={{ marginBottom:16 }}>I understand that TRIP students have created the current online platform through which student materials may be uploaded, stored, displayed, transmitted, or made accessible to authorized TRIP Industry Advisory Board members and other authorized recipients described above.</p>
                  <p style={{ marginBottom:16 }}>I understand and acknowledge that this platform is not itself a Texas A&amp;M University FERPA-compliant records system and may not provide the same privacy, security, access-control, retention, auditing, or data-protection features as official university systems. I nevertheless voluntarily consent to the upload, storage, maintenance, transmission, and access of my materials and information through this platform for the purposes described in this consent.</p>
                  <p style={{ marginBottom:16 }}>I understand that, once my materials are accessed, viewed, downloaded, printed, copied, saved, or further transmitted by authorized recipients, Texas A&amp;M University and TRIP may not be able to control or retrieve all copies of those materials.</p>

                  <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>E. Student responsibility for uploaded materials</div>
                  <p style={{ marginBottom:16 }}>I understand that I am responsible for deciding what materials to upload or provide. I should not upload or provide information that I do not want disclosed to TRIP Industry Advisory Board members or their company representatives.</p>
                  <p style={{ marginBottom:16 }}>I am encouraged to review all materials before uploading them and to remove or redact sensitive personal information that is not necessary for TRIP-related recruiting or professional-development purposes, such as Social Security numbers, passport numbers, driver's license numbers, personal financial information, medical information, or other sensitive information not requested by TRIP.</p>

                  <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>F. Voluntary consent and right to revoke</div>
                  <p style={{ marginBottom:16 }}>I understand that my consent is voluntary. I also understand that, under the Family Educational Rights and Privacy Act of 1974 ("FERPA"), personally identifiable information from my educational records generally may not be disclosed without my written consent unless an exception applies.</p>
                  <p style={{ marginBottom:16 }}>I understand that I may revoke this consent at any time by submitting a written request to the Director of the Adam C. Sinn '00 Center for Investment Management. I understand that revocation will not affect disclosures, access, downloads, copies, transmissions, or other actions already taken in reliance on this consent before the revocation was received and processed.</p>
                  <p style={{ marginBottom:16 }}>Unless revoked earlier in writing, this consent will remain in effect for as long as the current platform remains functional.</p>

                  <div style={{ fontWeight:700,color:"#1a1a1a",marginBottom:6,marginTop:20 }}>G. Acknowledgment</div>
                  <p>Through my electronic signature below, I confirm that I have read this consent form carefully, understand it, and voluntarily authorize the disclosure, access, storage, and use of the records, documents, and information described above for the purposes stated in this form.</p>
                </div>
              </div>

              {/* Signature block */}
              <div style={{ ...card, marginTop:16 }}>
                <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:18 }}>Electronic Signature</div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16 }}>
                  <div>
                    <label style={lbl}>Student Name</label>
                    <input value={`${form.firstName||""} ${form.lastName||""}`.trim()||"(set in Profile)"} readOnly style={{ ...input("fsn"),background:"#f5f5f5",color:"#555",cursor:"not-allowed" }} />
                  </div>
                  <div>
                    <label style={lbl}>UIN / Student ID <span style={{ color:"#dc2626" }}>*</span></label>
                    <input value={ferpaUIN} onChange={e=>setFerpaUIN(e.target.value.replace(/\D/g,"").slice(0,9))} placeholder="9-digit UIN" style={input("fuin")} onFocus={()=>setFocusedInput("fuin")} onBlur={()=>setFocusedInput(null)} />
                  </div>
                  <div>
                    <label style={lbl}>TAMU Email</label>
                    <input value={user?.email||""} readOnly style={{ ...input("femail"),background:"#f5f5f5",color:"#555",cursor:"not-allowed" }} />
                  </div>
                  <div>
                    <label style={lbl}>Date</label>
                    <input value={new Date().toLocaleDateString()} readOnly style={{ ...input("fdate"),background:"#f5f5f5",color:"#555",cursor:"not-allowed" }} />
                  </div>
                </div>
                <label style={{ display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",marginBottom:20,padding:"14px 16px",background:ferpaChecked?"#f0fdf4":"#f8f8f8",border:`1.5px solid ${ferpaChecked?"#86efac":"#e8e8e8"}`,borderRadius:10,transition:"all 0.2s" }}>
                  <input type="checkbox" checked={ferpaChecked} onChange={e=>setFerpaChecked(e.target.checked)} style={{ marginTop:2,width:16,height:16,accentColor:MAROON,flexShrink:0 }} />
                  <span style={{ fontSize:13,color:"#374151",lineHeight:1.6 }}>I have read this consent form carefully, understand it, and voluntarily authorize the disclosure, access, storage, and use of my records, documents, and information for the TRIP-related purposes stated above.</span>
                </label>
                <button type="button" disabled={!ferpaChecked||!ferpaUIN||ferpaSubmitting} onClick={async()=>{
                  if(!ferpaChecked||!ferpaUIN||!user)return;
                  setFerpaSubmitting(true);
                  try{
                    const ts=new Date().toISOString();
                    const nm=`${form.firstName||""} ${form.lastName||""}`.trim();
                    await setDoc(doc(db,"students",user.uid),{ferpaConsentStatus:"signed",ferpaSignedAt:ts,ferpaSignedName:nm,ferpaUIN,ferpaEmail:user.email},{merge:true});
                    updateForm("ferpaConsentStatus","signed");
                    updateForm("ferpaSignedAt",ts);
                    updateForm("ferpaSignedName",nm);
                    updateForm("ferpaUIN",ferpaUIN);
                    showToast("FERPA agreement signed — thank you!","success");
                    setActiveSection(nextSect||"academic");
                  }catch(err){showToast("Error: "+err.message,"error");}
                  setFerpaSubmitting(false);
                }} style={{ width:"100%",padding:"13px",background:ferpaChecked&&ferpaUIN?"linear-gradient(135deg,#6f0000,#a00000)":"#d1d5db",color:"white",border:"none",borderRadius:10,fontWeight:800,fontSize:14,cursor:ferpaChecked&&ferpaUIN?"pointer":"not-allowed",transition:"all 0.2s" }}>
                  {ferpaSubmitting?"Submitting…":"Sign & Submit →"}
                </button>
              </div>
            </>)}
          </div>
        )}

        {/* ACADEMIC */}
        {activeSection === "academic" && (
          <form onSubmit={e => handleSave(e, nextSect)} style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Academic & Background</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>This helps companies understand your availability and commitment to TRIP.</p>
            </div>
            <div style={card}>
              {form.group === "16" ? (
                <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:10,padding:"14px 18px",fontSize:13,color:"#166534" }}>
                  ✓ Group 16 is seeking full-time employment. No TRIP commitment or interview scheduling fields required.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 18 }}>TRIP Commitment</div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={lbl}>I will continue with TRIP for the next 12 months</label>
                    <div style={{ display: "flex", gap: 10 }}>
                      {["Yes","No","Unsure"].map(opt => (
                        <div key={opt} onClick={() => updateForm("continueTrip", opt)}
                          style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: `2px solid ${form.continueTrip === opt ? (opt === "Yes" ? "#22c55e" : opt === "No" ? "#dc2626" : "#f59e0b") : "#e8e8e8"}`, background: form.continueTrip === opt ? (opt === "Yes" ? "#f0fdf4" : opt === "No" ? "#fff1f0" : "#fffbeb") : "white", cursor: "pointer", textAlign: "center", fontWeight: 700, fontSize: 14, color: form.continueTrip === opt ? (opt === "Yes" ? "#166534" : opt === "No" ? "#991b1b" : "#92400e") : "#555", transition: "all 0.2s" }}>
                          {opt === "Yes" ? "✓ " : opt === "No" ? "✗ " : "? "}{opt}
                        </div>
                      ))}
                    </div>
                    {(form.continueTrip === "No" || form.continueTrip === "Unsure") && (
                      <div style={{ marginTop: 12, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#92400e", lineHeight: 1.6, animation: "slideIn 0.3s ease" }}>
                        If you feel that TRIP is not the right program for you or you are unsure, please contact Mrs. McLaughlin before proceeding further.
                      </div>
                    )}
                  </div>
                  {form.group === "18" && <div style={{ marginBottom: 20 }}>
                    <label style={lbl}>Summer or Spring Internship Exception (approved by Mrs. McLaughlin)</label>
                    <select value={form.springException} onChange={e => updateForm("springException", e.target.value)} style={input("springEx")}
                      onFocus={() => setFocusedInput("springEx")} onBlur={() => setFocusedInput(null)}>
                      <option value="">Select...</option>
                      <option>This does not apply to me</option>
                      <option>Senior year Corps of Cadets</option>
                      <option>Scholarship requirement</option>
                      <option>Capstone course for my major</option>
                      <option>Summer Internship Exception</option>
                      <option>Other</option>
                    </select>
                    {form.springException && form.springException !== "" && form.springException !== "This does not apply to me" && (
                      <div style={{ marginTop: 10, animation: "slideIn 0.3s ease" }}>
                        {form.springExceptionStatus === "pending" && (
                          <div style={{ background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#92400e",display:"flex",alignItems:"center",gap:8 }}>
                            ⏳ Exception request pending admin approval
                          </div>
                        )}
                        {form.springExceptionStatus === "approved" && (
                          <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#166534" }}>
                            ✓ Exception approved by admin
                          </div>
                        )}
                        {form.springExceptionStatus === "denied" && (
                          <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#991b1b" }}>
                            ✗ Exception denied — contact Mrs. McLaughlin
                          </div>
                        )}
                        {(!form.springExceptionStatus || form.springExceptionStatus === "") && (
                          <button type="button" onClick={async () => {
                            updateForm("springExceptionStatus", "pending");
                            await setDoc(doc(db, "students", user.uid), { springExceptionStatus: "pending", springExceptionRequestedAt: new Date().toISOString() }, { merge: true });
                          }} style={{ background:`linear-gradient(135deg,${MAROON},#a00000)`,color:"white",border:"none",padding:"9px 20px",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer" }}>
                            Submit Exception Request →
                          </button>
                        )}
                      </div>
                    )}
                  </div>}
                  <div>
                    <label style={lbl}>Interview Availability Conflict</label>
                    <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                      {["No","Yes"].map(opt => (
                        <div key={opt} onClick={() => updateForm("hasBlackoutRequest", opt)}
                          style={{ flex:1, padding:"12px 16px", borderRadius:10, border:`2px solid ${form.hasBlackoutRequest===opt?(opt==="Yes"?"#dc2626":MAROON):"#e8e8e8"}`, background:form.hasBlackoutRequest===opt?(opt==="Yes"?"#fff1f0":"#fff0f0"):"white", cursor:"pointer", textAlign:"center", fontWeight:700, fontSize:14, color:form.hasBlackoutRequest===opt?(opt==="Yes"?"#dc2626":MAROON):"#555", transition:"all 0.2s" }}>
                          {opt === "Yes" ? "Yes — I have conflicts" : "No — I am fully available"}
                        </div>
                      ))}
                    </div>
                    {form.hasBlackoutRequest === "Yes" && (
                      <div style={{ animation: "slideIn 0.3s ease" }}>
                        <div style={{ background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:10,padding:"12px 16px",marginBottom:12,fontSize:13,color:"#92400e" }}>
                          You must have prior approval from Mrs. McLaughlin before submitting a blackout request.
                        </div>
                        <label style={lbl}>Briefly explain your conflict</label>
                        <textarea value={form.blackoutReason||""} onChange={e => updateForm("blackoutReason", e.target.value)} rows={3}
                          placeholder="e.g. I have a mandatory class on Monday morning that Mrs. McLaughlin has approved..."
                          style={{ ...input("blackoutReason"), resize:"vertical", marginBottom:12 }}
                          onFocus={() => setFocusedInput("blackoutReason")} onBlur={() => setFocusedInput(null)} />
                        {form.blackoutRequestStatus === "pending" && (
                          <div style={{ background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#92400e",display:"flex",alignItems:"center",gap:8 }}>
                            ⏳ Request pending admin approval
                          </div>
                        )}
                        {form.blackoutRequestStatus === "approved" && (
                          <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#166534",display:"flex",alignItems:"center",gap:8 }}>
                            ✓ Approved — go to the Blackout Times tab to select your unavailable slots
                          </div>
                        )}
                        {form.blackoutRequestStatus === "denied" && (
                          <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#991b1b",display:"flex",alignItems:"center",gap:8 }}>
                            ✗ Request denied — contact Mrs. McLaughlin for further guidance
                          </div>
                        )}
                        {(!form.blackoutRequestStatus || form.blackoutRequestStatus === "") && form.blackoutReason && (
                          <button type="button" onClick={async () => {
                            updateForm("blackoutRequestStatus", "pending");
                            await setDoc(doc(db, "students", user.uid), { blackoutRequestStatus: "pending", blackoutReason: form.blackoutReason, blackoutRequestedAt: new Date().toISOString() }, { merge: true });
                          }} style={{ background:`linear-gradient(135deg,${MAROON},#a00000)`,color:"white",border:"none",padding:"11px 24px",borderRadius:10,fontWeight:700,fontSize:14,cursor:"pointer",marginTop:4 }}>
                            Submit Request to Admin →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {isLocked && <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600 }}>The submission deadline has passed. Your form is locked. Contact an admin to reopen.</div>}
            <SaveBar saving={saving} unsaved={unsaved} disabled={isLocked} nextLabel="Internships →" />
          </form>
        )}

        {/* INTERNSHIPS */}
        {activeSection === "internships" && (
          <form onSubmit={e => handleSave(e, nextSect)} style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>{form.group === "18" ? "Presentation" : "Internships"}</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>{form.group === "16" ? "Tell us about your internship experience" : "Tell us about your internship experience or presentation topic"}</p>
              {form.group === "18" && (
                <div style={{ marginTop: 10, background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#1d4ed8", lineHeight: 1.6 }}>
                  For Group 18, you may write about either your summer internship <em>or</em> your presentation topic — whichever you choose to feature in your resume book and live presentation. Your selection below will determine what your presentation is based on, so choose the one you plan to present.
                </div>
              )}
            </div>

            {form.group === "18" && (
              <div style={{ ...card, marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>Presentation Topic <span style={{ color:"#dc2626" }}>*</span></div>
                <div>
                  <select value={form.presentationTopic||""} onChange={e => updateForm("presentationTopic", e.target.value)} style={input("g18topic")} onFocus={() => setFocusedInput("g18topic")} onBlur={() => setFocusedInput(null)}>
                    <option value="">Select a topic…</option>
                    {(settings.presentationTopics || ["Natural Gas","Crude Oil","Power","Renewables","Petrochemicals","Other"]).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  {form.presentationTopic && (
                    <div style={{ marginTop: 10, padding: "10px 14px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, fontSize: 13, color: "#166534", fontWeight: 600 }}>
                      Topic selected: {form.presentationTopic}
                    </div>
                  )}
                </div>
              </div>
            )}

            {form.group !== "16" && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ ...lbl, fontSize: 12, marginBottom: 12 }}>What are you completing?</label>
                <div style={{ display: "flex", gap: 10 }}>
                  {internshipOptions.map(opt => (
                    <div key={opt.value} onClick={() => {
                      const newType = opt.value;
                      setInternshipType(newType);
                      setUnsaved(true);
                      if (form.group === "17") {
                        if (newType === "spring") setForm(f => ({ ...f, summerInternshipCompany: "", summerInternship: "", presentationLinkFor: "spring" }));
                        else if (newType === "summer") setForm(f => ({ ...f, springInternshipCompany: "", springInternship: "", presentationLinkFor: "summer" }));
                        else setForm(f => ({ ...f, presentationLinkFor: "" }));
                      }
                      if (form.group === "18") {
                        if (newType === "investment") setForm(f => ({ ...f, summerInternshipCompany: "", summerInternship: "" }));
                        else setForm(f => ({ ...f, investmentDecision: "" }));
                      }
                    }}
                      style={{ flex: 1, padding: "16px 12px", borderRadius: 12, border: `2px solid ${internshipType === opt.value ? MAROON : "#e8e8e8"}`, background: internshipType === opt.value ? "#fff0f0" : "white", cursor: "pointer", textAlign: "center", transition: "all 0.2s" }}
                      onMouseOver={e => { if (internshipType !== opt.value) e.currentTarget.style.borderColor = "#fca5a5"; }}
                      onMouseOut={e => { if (internshipType !== opt.value) e.currentTarget.style.borderColor = "#e8e8e8"; }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: internshipType === opt.value ? MAROON : "#f0f0f0", color: internshipType === opt.value ? "white" : "#555", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, margin: "0 auto 10px" }}>{opt.label}</div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: internshipType === opt.value ? MAROON : "#555" }}>{opt.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── GROUP 17: Spring / Summer / Both ── */}
            {form.group === "17" && (internshipType === "spring" || internshipType === "both") && (
              <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 16 }}>Spring Internship</div>
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Company Name</label>
                  <input value={form.springInternshipCompany} onChange={e => updateForm("springInternshipCompany", e.target.value)} style={input("sic")} onFocus={() => setFocusedInput("sic")} onBlur={() => setFocusedInput(null)} />
                </div>
                <div>
                  <label style={lbl}>Description</label>
                  <textarea value={form.springInternship} onChange={e => updateForm("springInternship", e.target.value.slice(0,g17DescMax))} rows={4} maxLength={g17DescMax} placeholder="Worked on financial modeling for energy markets…" style={{ ...input("si"), resize: "vertical" }} onFocus={() => setFocusedInput("si")} onBlur={() => setFocusedInput(null)} />
                  <div style={{ textAlign:"right",fontSize:11,color:form.springInternship?.length>g17DescMax?"#dc2626":"#aaa",marginTop:4 }}>{form.springInternship?.length||0} / {g17DescMax} chars</div>
                </div>
              </div>
            )}
            {form.group === "17" && (internshipType === "summer" || internshipType === "both") && (
              <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 16 }}>Summer Internship</div>
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Company Name</label>
                  <input value={form.summerInternshipCompany} onChange={e => updateForm("summerInternshipCompany", e.target.value)} style={input("sumic")} onFocus={() => setFocusedInput("sumic")} onBlur={() => setFocusedInput(null)} />
                </div>
                <div>
                  <label style={lbl}>Description</label>
                  <textarea value={form.summerInternship} onChange={e => updateForm("summerInternship", e.target.value.slice(0,g17DescMax))} rows={4} maxLength={g17DescMax} placeholder="Supported derivatives trading desk…" style={{ ...input("sumi"), resize: "vertical" }} onFocus={() => setFocusedInput("sumi")} onBlur={() => setFocusedInput(null)} />
                  <div style={{ textAlign:"right",fontSize:11,color:form.summerInternship?.length>g17DescMax?"#dc2626":"#aaa",marginTop:4 }}>{form.summerInternship?.length||0} / {g17DescMax} chars</div>
                </div>
              </div>
            )}

            {/* Presentation Video for Group 17 */}
            {form.group === "17" && (
              <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 16 }}>Presentation Video <span style={{ color:"#dc2626" }}>*</span></div>
                {internshipType === "both" && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={lbl}>Which internship is this video about? <span style={{ color:"#dc2626" }}>Required</span></label>
                    <div style={{ display: "flex", gap: 10 }}>
                      {[["spring", "Spring Internship"], ["summer", "Summer Internship"]].map(([val, title]) => (
                        <div key={val} onClick={() => updateForm("presentationLinkFor", val)}
                          style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: `2px solid ${form.presentationLinkFor === val ? MAROON : "#e8e8e8"}`, background: form.presentationLinkFor === val ? "#fff0f0" : "white", cursor: "pointer", textAlign: "center", fontWeight: 700, fontSize: 14, color: form.presentationLinkFor === val ? MAROON : "#555", transition: "all 0.2s" }}>
                          {title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <label style={lbl}>Video Link <span style={{ color:"#dc2626" }}>Required</span></label>
                  <input value={form.presentationLink||""} onChange={e => updateForm("presentationLink", e.target.value)} placeholder="https://..." style={input("pl17")} onFocus={() => setFocusedInput("pl17")} onBlur={() => setFocusedInput(null)} />
                </div>
              </div>
            )}

            {/* ── GROUP 18: Summer Internship OR Commodities Analysis ── */}
            {form.group === "18" && internshipType === "summer" && (
              <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 16 }}>Summer Internship</div>
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Company Name</label>
                  <input value={form.summerInternshipCompany} onChange={e => updateForm("summerInternshipCompany", e.target.value)} style={input("g18sumic")} onFocus={() => setFocusedInput("g18sumic")} onBlur={() => setFocusedInput(null)} />
                </div>
                <div>
                  <label style={lbl}>Description</label>
                  <textarea value={form.summerInternship} onChange={e => updateForm("summerInternship", e.target.value.slice(0,600))} rows={6} maxLength={600} placeholder="Supported derivatives trading desk…" style={{ ...input("g18sumi"), resize: "vertical" }} onFocus={() => setFocusedInput("g18sumi")} onBlur={() => setFocusedInput(null)} />
                </div>
              </div>
            )}
            {form.group === "18" && internshipType === "investment" && (
              <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Commodities Analysis</div>
                <div>
                  <label style={lbl}>Commodities Analysis Synopsis <span style={{ color:"#dc2626" }}>*</span></label>
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 12, lineHeight: 1.5 }}>Describe the paper you chose and give a brief synopsis in no more than 3 sentences or 70 words.</p>
                  <textarea value={form.investmentDecision} onChange={e => updateForm("investmentDecision", e.target.value.slice(0,600))} rows={8} maxLength={600} placeholder="e.g. Natural Gas: an analysis of…" style={{ ...input("inv"), resize: "vertical" }} onFocus={() => setFocusedInput("inv")} onBlur={() => setFocusedInput(null)} />
                  <div style={{ marginTop: 6, fontSize: 12, textAlign: "right", color: (form.investmentDecision||"").trim().split(/\s+/).filter(Boolean).length > 70 ? "#dc2626" : "#9ca3af" }}>
                    {(form.investmentDecision||"").trim().split(/\s+/).filter(Boolean).length} / 70 words
                  </div>
                </div>
              </div>
            )}


            {/* ── GROUP 16: 1, 2, or 3 Internships ── */}
            {form.group === "16" && (
              <>
                {/* Internship 1 — Spring or Summer toggle */}
                <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
                    <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6 }}>Internship 1</div>
                    <div style={{ display:"flex",gap:8 }}>
                      {["Spring","Summer"].map(opt => (
                        <button key={opt} type="button" onClick={() => { updateForm("internship1Season", opt); setUnsaved(true); }}
                          style={{ padding:"5px 16px",borderRadius:8,border:`1.5px solid ${(form.internship1Season||"Spring")===opt?MAROON:"#e8e8e8"}`,background:(form.internship1Season||"Spring")===opt?"#fff0f0":"white",color:(form.internship1Season||"Spring")===opt?MAROON:"#555",fontWeight:700,fontSize:12,cursor:"pointer",transition:"all 0.15s" }}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom:16 }}>
                    <label style={lbl}>Company Name</label>
                    <input value={form.springInternshipCompany} onChange={e => updateForm("springInternshipCompany", e.target.value)} style={input("g16i1c")} onFocus={() => setFocusedInput("g16i1c")} onBlur={() => setFocusedInput(null)} />
                  </div>
                  <div>
                    <label style={lbl}>Description</label>
                    <textarea value={form.springInternship} onChange={e => updateForm("springInternship", e.target.value.slice(0,150))} rows={3} maxLength={150} style={{ ...input("g16i1d"), resize:"vertical" }} onFocus={() => setFocusedInput("g16i1d")} onBlur={() => setFocusedInput(null)} />
                    <div style={{ textAlign:"right",fontSize:11,color:form.springInternship?.length>150?"#dc2626":"#aaa",marginTop:4 }}>{form.springInternship?.length||0} / 150 chars</div>
                  </div>
                </div>

                {/* Internship 2 — Spring (only if g16_two or g16_all) */}
                {(internshipType === "g16_two" || internshipType === "g16_all") && (
                  <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                    <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:16 }}>Internship 2 — Spring</div>
                    <div style={{ marginBottom:16 }}>
                      <label style={lbl}>Company Name</label>
                      <input value={form.summerInternshipCompany} onChange={e => updateForm("summerInternshipCompany", e.target.value)} style={input("g16i2c")} onFocus={() => setFocusedInput("g16i2c")} onBlur={() => setFocusedInput(null)} />
                    </div>
                    <div>
                      <label style={lbl}>Description</label>
                      <textarea value={form.summerInternship} onChange={e => updateForm("summerInternship", e.target.value.slice(0,150))} rows={3} maxLength={150} style={{ ...input("g16i2d"), resize:"vertical" }} onFocus={() => setFocusedInput("g16i2d")} onBlur={() => setFocusedInput(null)} />
                      <div style={{ textAlign:"right",fontSize:11,color:form.summerInternship?.length>150?"#dc2626":"#aaa",marginTop:4 }}>{form.summerInternship?.length||0} / 150 chars</div>
                    </div>
                  </div>
                )}

                {/* Internship 3 — Summer (only if g16_all) */}
                {internshipType === "g16_all" && (
                  <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                    <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:16 }}>Internship 3 — Summer</div>
                    <div style={{ marginBottom:16 }}>
                      <label style={lbl}>Company Name</label>
                      <input value={form.firstInternshipCompany||""} onChange={e => updateForm("firstInternshipCompany", e.target.value)} style={input("g16i3c")} onFocus={() => setFocusedInput("g16i3c")} onBlur={() => setFocusedInput(null)} />
                    </div>
                    <div>
                      <label style={lbl}>Description</label>
                      <textarea value={form.firstInternship||""} onChange={e => updateForm("firstInternship", e.target.value.slice(0,150))} rows={3} maxLength={150} style={{ ...input("g16i3d"), resize:"vertical" }} onFocus={() => setFocusedInput("g16i3d")} onBlur={() => setFocusedInput(null)} />
                      <div style={{ textAlign:"right",fontSize:11,color:form.firstInternship?.length>150?"#dc2626":"#aaa",marginTop:4 }}>{form.firstInternship?.length||0} / 150 chars</div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Presentation links for Group 16 - 2 required (for internships 2 and 3) */}
            {form.group === "16" && (internshipType === "g16_two" || internshipType === "g16_all") && (
              <div style={{ ...card, animation: "slideIn 0.3s ease" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 16 }}>Presentation Links <span style={{ color:"#dc2626" }}>*</span></div>
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Internship 2 — Spring Presentation Link <span style={{ color:"#dc2626" }}>Required</span></label>
                  <input value={form.presentationLink||""} onChange={e => updateForm("presentationLink", e.target.value)} placeholder="https://..." style={input("pl16a")} onFocus={() => setFocusedInput("pl16a")} onBlur={() => setFocusedInput(null)} required />
                </div>
                {internshipType === "g16_all" && (
                  <div>
                    <label style={lbl}>Internship 3 — Summer Presentation Link <span style={{ color:"#dc2626" }}>Required</span></label>
                    <input value={form.presentationLink2||""} onChange={e => updateForm("presentationLink2", e.target.value)} placeholder="https://..." style={input("pl16b")} onFocus={() => setFocusedInput("pl16b")} onBlur={() => setFocusedInput(null)} required />
                  </div>
                )}
              </div>
            )}

            {isLocked && <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600 }}>The submission deadline has passed. Your form is locked. Contact an admin to reopen.</div>}
            <SaveBar saving={saving} unsaved={unsaved} disabled={isLocked} nextLabel="Personal Statement →" />
          </form>
        )}

        {/* STATEMENT */}
        {activeSection === "statement" && (
          <form onSubmit={e => handleSave(e, nextSect)} style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Personal Statement</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Tell board members about yourself and your goals</p>
            </div>
            <div style={card}>
              <div style={{ background: "#f8f8f8", borderRadius: 10, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#666", lineHeight: 1.7 }}>
                <div style={{ fontWeight: 700, marginBottom: 6, color: "#444" }}>Consider including:</div>
                <div>• Your career goals in the energy industry</div>
                <div>• Relevant experience or coursework</div>
                <div>• What makes you a strong candidate for TRIP</div>
                <div>• Why you're interested in specific roles or companies</div>
              </div>
              <textarea
                value={form.personalStatement}
                onChange={e => { const val = e.target.value.slice(0, psMaxLen); updateForm("personalStatement", val); setCharCount(val.length); }}
                rows={12}
                placeholder="Share your goals, experience, and what makes you a strong candidate…"
                style={{ ...input("ps"), resize: "vertical" }}
                onFocus={() => setFocusedInput("ps")} onBlur={() => setFocusedInput(null)}
              />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 12 }}>
                <span style={{ color: "#aaa" }}>
                  {`Aim for under ${psMaxLen} characters`}
                </span>
                <span style={{ color: charCount < 100 ? "#f59e0b" : charCount > psMaxLen ? "#dc2626" : "#22c55e", fontWeight: 700 }}>{charCount.toLocaleString()} chars</span>
              </div>

            </div>
            {isLocked && <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600 }}>The submission deadline has passed. Your form is locked. Contact an admin to reopen.</div>}
            <SaveBar saving={saving} unsaved={unsaved} disabled={isLocked} nextLabel="Role Interests →" />
          </form>
        )}

        {/* INTERESTS */}
        {activeSection === "interests" && (
          <form onSubmit={e => handleSave(e, nextSect)} style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Role Interests</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Rate your interest in each role from 1 (not interested) to 10 (very interested)</p>
            </div>

            {/* Quick fill */}
            <div style={{ ...card, padding: "16px 24px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#555", marginRight: 4 }}>Quick set all to:</span>
              {[["Low (3)", 3],["Neutral (5)", 5],["High (8)", 8]].map(([label, val]) => (
                <button key={val} type="button" onClick={() => { const updates = {}; ALL_ROLES.forEach(([,key]) => updates[key] = val); setForm(f => ({...f,...updates})); setUnsaved(true); }}
                  style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid #e8e8e8", background: "white", color: "#555", fontWeight: 600, fontSize: 12, cursor: "pointer", transition: "all 0.15s" }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = MAROON; e.currentTarget.style.color = MAROON; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.color = "#555"; }}>
                  {label}
                </button>
              ))}
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#aaa" }}>Completed: {ALL_ROLES.filter(([,k]) => form[k] !== 5).length}/{ALL_ROLES.length} customized</span>
            </div>

            {ROLE_CATEGORIES.map(cat => (
              <div key={cat.category} style={card}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>{cat.category}</div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#d1d5db", marginBottom: 20 }}>
                  <span>Not Interested</span><span>Very Interested</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  {cat.roles.map(([label, key]) => (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <label style={{ width: 160, fontSize: 14, fontWeight: 600, color: "#333", flexShrink: 0 }}>{label}</label>
                      <input type="range" min="1" max="10" value={form[key]}
                        onChange={e => updateForm(key, parseInt(e.target.value))}
                        style={{ flex: 1, accentColor: MAROON }}
                        onInput={e => { const v = parseInt(e.target.value); e.target.style.background = `linear-gradient(to right, ${MAROON} 0%, ${MAROON} ${(v-1)/9*100}%, #f0f0f0 ${(v-1)/9*100}%, #f0f0f0 100%)`; }}
                      />
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: form[key] >= 8 ? `linear-gradient(135deg, ${MAROON}, #a00000)` : form[key] >= 5 ? "#f0f0f0" : "#f8f8f8", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14, color: form[key] >= 8 ? "white" : "#555", flexShrink: 0, transition: "all 0.2s", boxShadow: form[key] >= 8 ? "0 4px 10px rgba(111,0,0,0.3)" : "none" }}>
                        {form[key]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {isLocked && <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600 }}>The submission deadline has passed. Your form is locked. Contact an admin to reopen.</div>}
            <SaveBar saving={saving} unsaved={unsaved} disabled={isLocked} nextLabel="Documents →" />
          </form>
        )}

        {/* MY SCHEDULE */}
        {activeSection === "schedule" && schedulePublished && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>My Interview Schedule</h2>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>Your confirmed interviews for board week</p>
              </div>
              {mySchedule.length > 0 && (
                <button onClick={() => {
                  const studentName = `${form.firstName||""} ${form.lastName||""}`.trim();
                  const parseT = t => { const s=String(t||""); const ap=s.match(/(\d+):(\d+)\s*(AM|PM)/i); if(ap){let h=parseInt(ap[1]);const mn=parseInt(ap[2]);if(ap[3].toUpperCase()==="PM"&&h!==12)h+=12;if(ap[3].toUpperCase()==="AM"&&h===12)h=0;return h*60+mn;} const mil=s.match(/^(\d+):(\d+)$/);return mil?parseInt(mil[1])*60+parseInt(mil[2]):0; };
                  // item.day is the real board-week day label written by the scheduler — order by configured day order.
                  const dayLabel = dk => dk;
                  const configuredLabels = (settings.boardWeek?.days || []).map(d => d.label);
                  const distinctDays = [...new Set(mySchedule.map(i => i.day))];
                  const days = distinctDays.sort((a, b) => configuredLabels.indexOf(a) - configuredLabels.indexOf(b));
                  const html = `<html><head><title>TRIP Interview Schedule — ${studentName}</title><style>
                    body{font-family:Arial,sans-serif;margin:32px;color:#1a1a1a;font-size:13px}
                    h1{color:#6f0000;font-size:22px;margin:0 0 4px}
                    .sub{color:#666;font-size:13px;margin:0 0 6px}
                    .meta{color:#999;font-size:11px;margin:0 0 24px}
                    h2{font-size:14px;color:#6f0000;margin:24px 0 10px;border-bottom:2px solid #6f0000;padding-bottom:4px}
                    table{width:100%;border-collapse:collapse;margin-bottom:20px}
                    th{background:#6f0000;color:white;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px}
                    td{padding:9px 12px;border-bottom:1px solid #f0f0f0}
                    .time{font-weight:800;color:#6f0000;font-size:14px}
                    .conf{background:#f0fdf4;border:1px solid #86efac;color:#166534;padding:2px 8px;border-radius:12px;font-size:10px;font-weight:700}
                    @media print{body{margin:16px}}
                  </style></head><body>
                    <h1>My Interview Schedule</h1>
                    <p class="sub">${studentName}</p>
                    <p class="meta">Generated ${new Date().toLocaleString()}</p>
                    ${days.map(day => {
                      const items = mySchedule.filter(i => i.day === day).sort((a,b) => parseT(a.time)-parseT(b.time));
                      return `<h2>${dayLabel(day)}</h2>
                        <table><thead><tr><th>Time</th><th>Company</th><th>Room</th><th>Status</th></tr></thead><tbody>
                        ${items.map(item => `<tr><td class="time">${fmt12(item.time)}</td><td><b>${item.company||""}</b></td><td>Room ${String(item.room).replace("Room ","")}</td><td><span class="conf">✓ Confirmed</span></td></tr>`).join("")}
                        </tbody></table>`;
                    }).join("")}
                  </body></html>`;
                  const w = window.open("","_blank"); w.document.write(html); w.document.close(); w.focus(); w.print();
                }} style={{ background: `linear-gradient(135deg,${MAROON},#a00000)`, color: "white", border: "none", padding: "10px 20px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: "0 4px 14px rgba(111,0,0,0.25)" }}>
                  ↓ Download PDF
                </button>
              )}
            </div>
            {mySchedule.length === 0 ? (
              <div style={{ background: "white", borderRadius: 16, padding: "40px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>📅</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#333", marginBottom: 8 }}>No interviews scheduled</div>
                <div style={{ color: "#9ca3af", fontSize: 14 }}>You don't have any interviews scheduled yet. Check back later.</div>
              </div>
            ) : (
              <div>
                {/* Stats row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
                  <div style={{ background: `linear-gradient(135deg, ${MAROON}, #a00000)`, borderRadius: 14, padding: "20px 24px", color: "white", boxShadow: "0 4px 16px rgba(111,0,0,0.25)" }}>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Interviews</div>
                    <div style={{ fontSize: 44, fontWeight: 900, letterSpacing: -2, lineHeight: 1 }}>{mySchedule.length}</div>
                  </div>
                  <div style={{ background: "white", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0" }}>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Interview Days</div>
                    <div style={{ fontSize: 44, fontWeight: 900, letterSpacing: -2, lineHeight: 1, color: "#1a1a1a" }}>{[...new Set(mySchedule.map(i => i.day))].length}</div>
                  </div>
                </div>

                {/* Timeline per day — item.day is the real board-week day label written by the scheduler. */}
                {(() => {
                  const configuredLabels = (settings.boardWeek?.days || []).map(d => d.label);
                  const distinctDays = [...new Set(mySchedule.map(i => i.day))];
                  return distinctDays.sort((a, b) => configuredLabels.indexOf(a) - configuredLabels.indexOf(b));
                })().map((day, dayIdx) => {
                  const parseT = t => { const s=String(t||""); const ap=s.match(/(\d+):(\d+)\s*(AM|PM)/i); if(ap){let h=parseInt(ap[1]);const mn=parseInt(ap[2]);if(ap[3].toUpperCase()==="PM"&&h!==12)h+=12;if(ap[3].toUpperCase()==="AM"&&h===12)h=0;return h*60+mn;} const mil=s.match(/^(\d+):(\d+)$/);return mil?parseInt(mil[1])*60+parseInt(mil[2]):0; };
                  const dayItems = mySchedule.filter(i => i.day === day).sort((a,b) => parseT(a.time)-parseT(b.time));
                  if (!dayItems.length) return null;
                  const dotColors = ["#6f0000","#b45309","#0369a1","#065f46","#7c3aed","#be185d","#0e7490"];
                  return (
                    <div key={day} style={{ marginBottom: 32 }}>
                      {/* Day header */}
                      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
                        <div style={{ background: `linear-gradient(135deg, ${MAROON}, #a00000)`, color: "white", width: 42, height: 42, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 16, flexShrink: 0, boxShadow: "0 4px 14px rgba(111,0,0,0.3)" }}>{dayIdx+1}</div>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 18, color: "#1a1a1a" }}>{day}</div>
                          <div style={{ fontSize: 13, color: "#9ca3af" }}>{dayItems.length} interview{dayItems.length!==1?"s":""}</div>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div style={{ position: "relative", paddingLeft: 40 }}>
                        {/* Vertical connector line */}
                        <div style={{ position: "absolute", left: 20, top: 8, bottom: 8, width: 2, background: `linear-gradient(to bottom, ${MAROON}55, ${MAROON}10)`, borderRadius: 2 }} />

                        {dayItems.map((item, i) => {
                          const color = dotColors[i % dotColors.length];
                          return (
                            <div key={i} style={{ position: "relative", marginBottom: i < dayItems.length-1 ? 16 : 0 }}>
                              {/* Dot */}
                              <div style={{ position: "absolute", left: -29, top: 18, width: 14, height: 14, borderRadius: "50%", background: color, boxShadow: `0 0 0 3px white, 0 0 0 5px ${color}44`, zIndex: 1 }} />

                              {/* Card */}
                              <div style={{ background: "white", borderRadius: 14, padding: "16px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 16, transition: "box-shadow 0.2s" }}
                                onMouseOver={e=>e.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,0.1)"}
                                onMouseOut={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.06)"}>
                                {/* Time chip */}
                                <div style={{ background: `${color}12`, border: `1.5px solid ${color}30`, borderRadius: 10, padding: "10px 14px", textAlign: "center", flexShrink: 0, minWidth: 72 }}>
                                  <div style={{ fontWeight: 900, fontSize: 15, color, letterSpacing: -0.3 }}>{fmt12(item.time)}</div>
                                  <div style={{ fontSize: 10, color: `${color}99`, fontWeight: 700, marginTop: 1 }}>30 min</div>
                                </div>
                                {/* Info */}
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 800, fontSize: 15, color: "#1a1a1a", marginBottom: 6 }}>{item.company}</div>
                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 12, color: "#6b7280", background: "#f3f4f6", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>📍 Room {String(item.room).replace("Room ","")}</span>
                                  </div>
                                </div>
                                {/* Badge */}
                                <div style={{ background: "#f0fdf4", color: "#166534", border: "1.5px solid #86efac", padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, flexShrink: 0, whiteSpace: "nowrap" }}>✓ Confirmed</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PRESENTATION SLOT — shown when presentations are published for Group 17/18 */}
        {activeSection === "schedule" && presentationsPublished && form.group === "18" && (
          <div style={{ animation: "fadeUp 0.4s ease both", marginTop: 28 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: "#0f0f0f", marginBottom: 16, letterSpacing: -0.5 }}>My Presentation Slot</h2>
            {myPresentationSlot ? (
              <div style={{ background: "white", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: `2px solid ${MAROON}22`, display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ background: `linear-gradient(135deg,${MAROON},#a00000)`, color: "white", padding: "12px 18px", borderRadius: 10, textAlign: "center", minWidth: 80 }}>
                  <div style={{ fontSize: 20, fontWeight: 900 }}>{myPresentationSlot.slot}</div>
                  <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>{myPresentationSlot.duration} min</div>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: "#1a1a1a" }}>{myPresentationSlot.room}</div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>Topic: {myPresentationSlot.topic}</div>
                </div>
                <div style={{ marginLeft: "auto", background: "#f0fdf4", color: "#166534", border: "1.5px solid #86efac", padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>✓ Confirmed</div>
              </div>
            ) : (
              <div style={{ background: "#f9fafb", border: "1px solid #e8e8e8", borderRadius: 12, padding: "20px 24px", fontSize: 13, color: "#9ca3af" }}>
                Your presentation slot has not been assigned yet. Check back later.
              </div>
            )}
          </div>
        )}

        {/* DRAFT RESULTS */}
        {activeSection === "results" && (myDraftResults.length > 0 || draftResultsPublished) && (() => {
          const requiredPicks = form.group === "17" ? 2 : 1;
          const stillNeeded = requiredPicks - myDraftResults.length;
          const seasonLabel = s => s === "spring" ? "Spring" : s === "summer" ? "Summer" : null;
          return (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            {/* Confetti burst whenever a new pick arrives */}
            {myDraftResults.length > confettiFiredCount && (() => {
              setConfettiFiredCount(myDraftResults.length);
              fireConfetti();
              return null;
            })()}

            {myDraftResults.length > 0 ? (
              /* ── DRAFTED (one or more picks) ── */
              <div>
                {requiredPicks > 1 && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    {Array.from({length:requiredPicks}).map((_,i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8, flex:1 }}>
                        <div style={{ width:30,height:30,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,flexShrink:0,transition:"all 0.4s ease",
                          background: i < myDraftResults.length ? "linear-gradient(135deg,#22c55e,#16a34a)" : "#f0f0f0",
                          color: i < myDraftResults.length ? "white" : "#bbb",
                          boxShadow: i < myDraftResults.length ? "0 0 0 4px rgba(34,197,94,0.15)" : "none" }}>
                          {i < myDraftResults.length ? "✓" : i+1}
                        </div>
                        <div style={{ fontSize:12, fontWeight:700, color: i < myDraftResults.length ? "#166534" : "#9ca3af" }}>
                          Internship {i+1} {i < myDraftResults.length ? "secured" : "pending"}
                        </div>
                        <div style={{ flex:1, height:2, borderRadius:1, transition:"background 0.4s ease",
                          background: i === requiredPicks-1
                            ? (i < myDraftResults.length ? "#22c55e" : "#f0f0f0")
                            : (i < myDraftResults.length-1 ? "#22c55e" : "#f0f0f0") }} />
                      </div>
                    ))}
                  </div>
                )}

                {myDraftResults.length === 1 ? (
                  <div className="draft-hero" style={{ background:"linear-gradient(135deg,#0f0f0f,#1a0a0a,#2a0000)", borderRadius:20, padding:"48px 40px", marginBottom:24, color:"white", position:"relative", overflow:"hidden", boxShadow:"0 20px 60px rgba(111,0,0,0.5)" }}>
                    {/* Faint roaming red fluid */}
                    <div className="hero-fluid" style={{ width:280, height:280, background:"radial-gradient(circle, rgba(160,0,0,0.5) 0%, rgba(160,0,0,0) 70%)", filter:"blur(70px)", opacity:0.35, animation:"heroFluidA 30s ease-in-out infinite" }} />
                    <div className="hero-fluid" style={{ width:220, height:220, background:"radial-gradient(circle, rgba(210,40,40,0.45) 0%, rgba(210,40,40,0) 70%)", filter:"blur(60px)", opacity:0.28, animation:"heroFluidB 36s ease-in-out infinite" }} />
                    {/* Glow orb */}
                    <div style={{ position:"absolute",top:-60,right:-60,width:220,height:220,borderRadius:"50%",background:"radial-gradient(circle,rgba(111,0,0,0.6) 0%,transparent 70%)",pointerEvents:"none" }} />
                    <div style={{ position:"absolute",bottom:-80,left:-40,width:260,height:260,borderRadius:"50%",background:"radial-gradient(circle,rgba(217,119,6,0.25) 0%,transparent 70%)",pointerEvents:"none" }} />
                    {/* Shine sweep */}
                    <div className="draft-shine" />
                    {/* Sparkles */}
                    <span className="draft-sparkle" style={{ top:"14%", left:"58%", fontSize:16, animationDelay:"0s" }}>✨</span>
                    <span className="draft-sparkle" style={{ top:"62%", left:"90%", fontSize:20, animationDelay:"0.6s" }}>✨</span>
                    <span className="draft-sparkle" style={{ top:"78%", left:"48%", fontSize:12, animationDelay:"1.2s" }}>✨</span>
                    <span className="draft-sparkle" style={{ top:"8%", left:"85%", fontSize:14, animationDelay:"1.8s" }}>✨</span>
                    <div style={{ position:"relative" }}>
                      <div style={{ fontSize:12,fontWeight:700,letterSpacing:3,textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:20,display:"flex",alignItems:"center",gap:8 }}>
                        <span style={{ width:6,height:6,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 8px #4ade80",animation:"pulse 1.6s ease infinite" }} />
                        TRIP Board Week · Draft Results
                      </div>
                      <div className="draft-title-glow" style={{ fontSize:44,fontWeight:900,letterSpacing:-2,lineHeight:1.1,marginBottom:8, backgroundImage:"linear-gradient(90deg,#ffffff 0%,#ffd8a8 25%,#ff8080 50%,#ffd8a8 75%,#ffffff 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>You've been<br/>selected.</div>
                      <div style={{ fontSize:16,color:"rgba(255,255,255,0.5)",marginBottom:32 }}>{form.firstName} {form.lastName}</div>
                      <div style={{ display:"inline-flex",alignItems:"center",gap:16,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:14,padding:"18px 28px" }}>
                        <div style={{ position:"relative", width:48, height:48, flexShrink:0 }}>
                          <div className="draft-ring" />
                          <div className="draft-ring" style={{ animationDelay:"0.7s" }} />
                          <div style={{ position:"relative", width:48,height:48,borderRadius:"50%",background:`linear-gradient(135deg,${MAROON},#a00000)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,boxShadow:"0 0 20px rgba(180,0,0,0.6)" }}>🏢</div>
                        </div>
                        <div>
                          <div style={{ fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4 }}>Selected by</div>
                          <div style={{ fontSize:22,fontWeight:900,color:"white",letterSpacing:-0.5 }}>{myDraftResults[0].company}</div>
                        </div>
                      </div>
                      <div style={{ marginTop:20,display:"flex",flexWrap:"wrap",gap:8 }}>
                        <div style={{ display:"inline-flex",alignItems:"center",gap:8,background:"rgba(22,163,74,0.15)",border:"1px solid rgba(22,163,74,0.3)",borderRadius:20,padding:"6px 16px" }}>
                          <div style={{ width:7,height:7,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 8px #4ade80" }} />
                          <span style={{ fontSize:12,fontWeight:700,color:"#4ade80",textTransform:"uppercase",letterSpacing:1 }}>
                            {myDraftResults[0].round.charAt(0).toUpperCase()+myDraftResults[0].round.slice(1)} Round Pick
                          </span>
                        </div>
                        {seasonLabel(myDraftResults[0].season) && (
                          <div style={{ display:"inline-flex",alignItems:"center",background:"rgba(20,184,166,0.15)",border:"1px solid rgba(20,184,166,0.35)",borderRadius:20,padding:"6px 16px" }}>
                            <span style={{ fontSize:12,fontWeight:700,color:"#5eead4",textTransform:"uppercase",letterSpacing:1 }}>{seasonLabel(myDraftResults[0].season)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="draft-hero" style={{ background:"linear-gradient(135deg,#0f0f0f,#1a0a0a,#2a0000)", borderRadius:20, padding:"48px 40px", marginBottom:24, color:"white", position:"relative", overflow:"hidden", boxShadow:"0 20px 70px rgba(217,119,6,0.35), 0 20px 60px rgba(111,0,0,0.5)" }}>
                    {/* Faint roaming red fluid */}
                    <div className="hero-fluid" style={{ width:320, height:320, background:"radial-gradient(circle, rgba(160,0,0,0.5) 0%, rgba(160,0,0,0) 70%)", filter:"blur(75px)", opacity:0.32, animation:"heroFluidA 32s ease-in-out infinite" }} />
                    <div className="hero-fluid" style={{ width:260, height:260, background:"radial-gradient(circle, rgba(210,40,40,0.45) 0%, rgba(210,40,40,0) 70%)", filter:"blur(65px)", opacity:0.26, animation:"heroFluidB 38s ease-in-out infinite" }} />
                    {/* Dual glow orbs — one per internship */}
                    <div style={{ position:"absolute",top:-70,right:-70,width:260,height:260,borderRadius:"50%",background:"radial-gradient(circle,rgba(111,0,0,0.6) 0%,transparent 70%)",pointerEvents:"none" }} />
                    <div style={{ position:"absolute",bottom:-90,left:-50,width:300,height:300,borderRadius:"50%",background:"radial-gradient(circle,rgba(217,119,6,0.35) 0%,transparent 70%)",pointerEvents:"none" }} />
                    <div className="draft-shine" />
                    {/* Sparkles — kept to the header zone and outer edges so they never sit over the company cards */}
                    <span className="draft-sparkle" style={{ top:"6%", left:"62%", fontSize:16, animationDelay:"0s" }}>✨</span>
                    <span className="draft-sparkle" style={{ top:"12%", left:"92%", fontSize:20, animationDelay:"0.4s" }}>✨</span>
                    <span className="draft-sparkle" style={{ top:"4%", left:"14%", fontSize:12, animationDelay:"1.8s" }}>🎉</span>
                    <span className="draft-sparkle" style={{ top:"97%", left:"3%", fontSize:16, animationDelay:"1.3s" }}>✨</span>
                    <span className="draft-sparkle" style={{ top:"97%", left:"96%", fontSize:14, animationDelay:"2.2s" }}>🎉</span>
                    <div style={{ position:"relative" }}>
                      <div style={{ fontSize:12,fontWeight:700,letterSpacing:3,textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:18,display:"flex",alignItems:"center",gap:8 }}>
                        <span style={{ width:6,height:6,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 8px #4ade80",animation:"pulse 1.6s ease infinite" }} />
                        TRIP Board Week · Draft Results
                      </div>
                      <div style={{ display:"inline-flex",alignItems:"center",gap:8,background:"rgba(217,119,6,0.18)",border:"1px solid rgba(251,191,36,0.4)",borderRadius:20,padding:"6px 16px",marginBottom:18 }}>
                        <span style={{ fontSize:14 }}>🏆</span>
                        <span style={{ fontSize:12,fontWeight:800,color:"#fbbf24",textTransform:"uppercase",letterSpacing:1 }}>Fully Drafted</span>
                      </div>
                      <div className="draft-title-glow" style={{ fontSize:44,fontWeight:900,letterSpacing:-2,lineHeight:1.1,marginBottom:8, backgroundImage:"linear-gradient(90deg,#ffffff 0%,#ffd8a8 25%,#ff8080 50%,#ffd8a8 75%,#ffffff 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>You've been<br/>drafted twice.</div>
                      <div style={{ fontSize:16,color:"rgba(255,255,255,0.5)",marginBottom:32 }}>{form.firstName} {form.lastName}</div>

                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                        {myDraftResults.map((pick,i) => (
                          <div key={i} style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:14, padding:"18px 20px", animation:`draftPopIn 0.6s cubic-bezier(0.16,1,0.3,1) ${0.2+i*0.2}s both` }}>
                            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                              <div style={{ position:"relative", width:40, height:40, flexShrink:0 }}>
                                <div className="draft-ring" style={{ animationDelay:`${i*0.5}s` }} />
                                <div style={{ position:"relative", width:40,height:40,borderRadius:"50%",background:`linear-gradient(135deg,${MAROON},#a00000)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,boxShadow:"0 0 16px rgba(180,0,0,0.6)" }}>🏢</div>
                              </div>
                              <div>
                                <div style={{ fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:1,marginBottom:2 }}>Selected by</div>
                                <div style={{ fontSize:16,fontWeight:900,color:"white",letterSpacing:-0.3 }}>{pick.company}</div>
                              </div>
                            </div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                              <span style={{ fontSize:10.5, fontWeight:700, padding:"3px 10px", borderRadius:20, background:"rgba(22,163,74,0.18)", color:"#4ade80", border:"1px solid rgba(22,163,74,0.35)", textTransform:"capitalize" }}>{pick.round} Round</span>
                              {seasonLabel(pick.season) && <span style={{ fontSize:10.5, fontWeight:700, padding:"3px 10px", borderRadius:20, background:"rgba(20,184,166,0.18)", color:"#5eead4", border:"1px solid rgba(20,184,166,0.35)" }}>{seasonLabel(pick.season)}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {stillNeeded > 0 && (
                  <div style={{ background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:14, padding:"18px 22px", marginBottom:20, display:"flex", alignItems:"flex-start", gap:14 }}>
                    <div style={{ fontSize:24, lineHeight:1, animation:"draftFloat 2.4s ease-in-out infinite" }}>⏳</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:800, fontSize:14, color:"#92400e", marginBottom:4 }}>
                        {stillNeeded === 1 ? "One more internship to go" : `${stillNeeded} more internships to go`}
                      </div>
                      <div style={{ fontSize:13, color:"#92400e", opacity:0.85, lineHeight:1.6 }}>
                        You're eligible for another internship — you'll be interviewing in the Gold + Silver round for it.
                        {draftNextRoundDate && <> Check back <strong>{new Date(draftNextRoundDate).toLocaleString(undefined,{weekday:"long",month:"long",day:"numeric",hour:"numeric",minute:"2-digit"})}</strong> for that schedule.</>}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ background:"white",borderRadius:16,padding:"24px 28px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",border:"1px solid #f0f0f0" }}>
                  <div style={{ fontSize:14,color:"#6b7280",lineHeight:1.7 }}>
                    Congratulations! You have been selected by {myDraftResults.map((p,i) => (
                      <React.Fragment key={i}><strong style={{ color:"#1a1a1a" }}>{p.company}</strong>{i < myDraftResults.length-1 ? (i === myDraftResults.length-2 ? " and " : ", ") : ""}</React.Fragment>
                    ))}. You can expect to hear from {myDraftResults.length > 1 ? "them" : "them"} regarding next steps and internship details. Welcome to the team!
                  </div>
                </div>
              </div>
            ) : (
              /* ── NOT DRAFTED ── */
              <div>
                <div style={{ background:"linear-gradient(135deg,#0f0f0f,#111827)", borderRadius:20, padding:"48px 40px", marginBottom:24, color:"white", position:"relative", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
                  {/* Faint roaming red fluid */}
                  <div className="hero-fluid" style={{ width:280, height:280, background:"radial-gradient(circle, rgba(160,0,0,0.45) 0%, rgba(160,0,0,0) 70%)", filter:"blur(75px)", opacity:0.22, animation:"heroFluidA 34s ease-in-out infinite" }} />
                  <div className="hero-fluid" style={{ width:220, height:220, background:"radial-gradient(circle, rgba(210,40,40,0.4) 0%, rgba(210,40,40,0) 70%)", filter:"blur(65px)", opacity:0.18, animation:"heroFluidB 40s ease-in-out infinite" }} />
                  <div style={{ position:"relative" }}>
                    <div style={{ fontSize:12,fontWeight:700,letterSpacing:3,textTransform:"uppercase",color:"rgba(255,255,255,0.3)",marginBottom:20 }}>TRIP Board Week · Draft Results</div>
                    <div style={{ fontSize:40,fontWeight:900,letterSpacing:-2,lineHeight:1.1,marginBottom:16 }}>Round One<br/>Complete.</div>
                    <div style={{ fontSize:15,color:"rgba(255,255,255,0.5)",lineHeight:1.7,maxWidth:480 }}>
                      You were not selected in this round. Check back for your next round interview schedule.
                    </div>
                    {draftNextRoundDate && (
                      <div style={{ marginTop:28,display:"inline-flex",alignItems:"center",gap:12,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:14,padding:"14px 22px" }}>
                        <div style={{ fontSize:22 }}>📅</div>
                        <div>
                          <div style={{ fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:1,marginBottom:3 }}>Next Round Schedule Available</div>
                          <div style={{ fontSize:16,fontWeight:800,color:"white" }}>{new Date(draftNextRoundDate).toLocaleString(undefined,{weekday:"long",month:"long",day:"numeric",hour:"numeric",minute:"2-digit"})}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {/* BLACKOUT TIMES - only visible after admin approval */}
        {activeSection === "blackout" && form.blackoutRequestStatus === "approved" && (
          <form onSubmit={e => handleSave(e, null)} style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Blackout Times</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Select the specific time slots you are NOT available for interviews</p>
            </div>
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#166534" }}>
              ✓ Your blackout request has been approved. Select your unavailable time slots below.
            </div>
            <div style={{ background: "white", borderRadius: 16, padding: "24px 28px", marginBottom: 22, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <BlackoutPicker value={form.blackoutTimes || []} onChange={v => updateForm("blackoutTimes", v)} settings={settings} />
            </div>
            {isLocked && <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600 }}>⚠ The submission deadline has passed. Your form is locked. Contact an admin to reopen.</div>}
            <SaveBar saving={saving} unsaved={unsaved} disabled={isLocked} nextLabel={null} finalLabel="Save Blackout Times →" />
          </form>
        )}

        {/* DOCUMENTS */}
        {activeSection === "documents" && (
          <form onSubmit={e => handleSave(e, null)} style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Documents</h2>
              <p style={{ color: "#9ca3af", fontSize: 14 }}>Upload your resume and a professional headshot</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* Resume */}
              <div style={{ ...card, textAlign: "center", border: `2px dashed ${resumeFile ? MAROON : "#e0e0e0"}`, cursor: "pointer", transition: "all 0.2s" }}
                onMouseOver={e => { e.currentTarget.style.borderColor = MAROON; e.currentTarget.style.background = "#fff8f8"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = resumeFile ? MAROON : "#e0e0e0"; e.currentTarget.style.background = "white"; }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6, color: "#1a1a1a" }}>Resume PDF</div>
                <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>PDF format only</div>
                {form.resumeURL && !resumeFile && (
                  <div style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, display: "inline-block", marginBottom: 12 }}>✓ Uploaded</div>
                )}
                {resumeFile && (
                  <div style={{ background: "#fff0f0", border: "1px solid #fca5a5", color: MAROON, padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, display: "inline-block", marginBottom: 12 }}>📎 {resumeFile.name}</div>
                )}
                <div>
                  <label style={{ display: "inline-block", padding: "9px 20px", background: MAROON, color: "white", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {form.resumeURL ? "Replace File" : "Choose File"}
                    <input type="file" accept=".pdf" onChange={e => { setResumeFile(e.target.files[0]); setUnsaved(true); }} style={{ display: "none" }} />
                  </label>
                </div>
              </div>

              {/* Photo */}
              <div style={{ ...card, textAlign: "center", border: `2px dashed ${photoFile ? MAROON : "#e0e0e0"}`, cursor: "pointer", transition: "all 0.2s" }}
                onMouseOver={e => { e.currentTarget.style.borderColor = MAROON; e.currentTarget.style.background = "#fff8f8"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = photoFile ? MAROON : "#e0e0e0"; e.currentTarget.style.background = "white"; }}>
                {photoPreview ? (
                  <div style={{ marginBottom: 12 }}>
                    <img src={photoPreview} alt="headshot" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: `3px solid ${MAROON}`, boxShadow: "0 4px 12px rgba(111,0,0,0.2)" }} />
                  </div>
                ) : (
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🖼️</div>
                )}
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6, color: "#1a1a1a" }}>Headshot Photo</div>
                <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>JPG or PNG · Professional photo</div>
                {form.photoURL && !photoFile && (
                  <div style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, display: "inline-block", marginBottom: 12 }}>✓ Uploaded</div>
                )}
                {photoFile && (
                  <div style={{ background: "#fff0f0", border: "1px solid #fca5a5", color: MAROON, padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, display: "inline-block", marginBottom: 12 }}>📎 {photoFile.name}</div>
                )}
                <div>
                  <label style={{ display: "inline-block", padding: "9px 20px", background: MAROON, color: "white", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {form.photoURL ? "Replace Photo" : "Choose Photo"}
                    <input type="file" accept=".jpg,.jpeg,.png" onChange={e => { const file = e.target.files[0]; if (!file) return; if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview); setPhotoFile(file); setUnsaved(true); setPhotoPreview(URL.createObjectURL(file)); }} style={{ display: "none" }} />
                  </label>
                </div>
              </div>
            </div>
            {isLocked && <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600 }}>⚠ The submission deadline has passed. Your form is locked. Contact an admin to reopen.</div>}
            <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={handleSubmitAll} disabled={saving || isLocked}
                style={{ background: (saving || isLocked) ? "#d1d5db" : `linear-gradient(135deg, ${MAROON}, #a00000)`, color: "white", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: (saving || isLocked) ? "not-allowed" : "pointer", boxShadow: (saving || isLocked) ? "none" : "0 4px 14px rgba(111,0,0,0.25)", transition: "all 0.2s" }}>
                {saving ? "Submitting..." : "Submit All →"}
              </button>
              {settings.submissionDeadline && (
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  You can make changes until {new Date(settings.submissionDeadline + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} at {settings.submissionDeadlineTime || "11:59 PM"}.
                </span>
              )}
            </div>
          </form>
        )}
      </div>

      {/* Session timeout warning */}
      {sessionCountdown !== null && (
        <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#0f172a", border: "1px solid rgba(239,68,68,0.4)", color: "white", padding: "14px 24px", borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", display: "flex", alignItems: "center", gap: 14, zIndex: 300, animation: "slideIn 0.3s ease" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", animation: "pulse 1s ease infinite", flexShrink: 0 }} />
          Session expires in {Math.floor(sessionCountdown / 60)}:{String(sessionCountdown % 60).padStart(2, "0")}
          <button onClick={() => { signOut(auth); navigate("/august"); }} style={{ background: "#ef4444", color: "white", border: "none", padding: "5px 14px", borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: "pointer", marginLeft: 4 }}>Sign Out</button>
        </div>
      )}

      {/* Sticky unsaved indicator */}
      {unsaved && activeSection !== "home" && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: "#1a1a1a", color: "white", padding: "12px 20px", borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", gap: 10, zIndex: 200, animation: "slideIn 0.3s ease" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", animation: "pulse 1.5s ease infinite" }} />
          Unsaved changes
          <button onClick={e => handleSave(e, null)} style={{ background: MAROON, color: "white", border: "none", padding: "6px 14px", borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: "pointer", marginLeft: 4 }}>Save Now</button>
        </div>
      )}
    </div>
  );
}

function SaveBar({ saving, unsaved, nextLabel, finalLabel, disabled }) {
  const MAROON = "#6f0000";
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 4 }}>
      <button type="submit" disabled={saving || disabled}
        style={{ background: `linear-gradient(135deg, ${MAROON}, #a00000)`, color: "white", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: (saving || disabled) ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1, boxShadow: "0 4px 14px rgba(111,0,0,0.25)", display: "flex", alignItems: "center", gap: 8, transition: "all 0.2s" }}
        onMouseOver={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(111,0,0,0.35)"; }}
        onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(111,0,0,0.25)"; }}>
        {saving ? (
          <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Saving...</>
        ) : (
          finalLabel ? finalLabel : nextLabel ? `Save & Continue: ${nextLabel}` : "Save →"
        )}
      </button>
      {unsaved && <span style={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>● Unsaved changes</span>}
    </div>
  );
}
